/**
 * Active-client resolution + hydration gate (multi-client plan).
 *
 * Mounted inside AuthProvider, wrapping the router. Responsibilities:
 *
 *  1. Resolve the active client exactly once per page load:
 *       flag off            → null scope (today's behaviour)
 *       seed mode           → stored pick or the "local" stub client
 *       client-org user     → org id from token claims (server enforces)
 *       DXC home + pick     → the per-tab sessionStorage pick
 *       DXC home, no pick   → UNRESOLVED — render ClientSelectGate
 *  2. Run the boot hydrates (storage migration + asset locker + captures)
 *     only AFTER resolution, so no IndexedDB read ever happens under the
 *     wrong (or an unresolved) client key. This is the ordering guarantee
 *     `client-scoped-idb.ts` relies on.
 *
 * In zitadel mode with no user yet, we render children unresolved (the
 * AppLayout gate shows LoginPage) and skip hydration — the OIDC redirect
 * makes login a fresh page load, so resolution re-runs with the user.
 */

import { useEffect, useRef } from "react";
import { isMultiClientEnabled, isZitadelMode } from "@/env";
import { clientOrgIdFromClaims } from "@/features/auth/roles";
import { getCurrentUser } from "@/features/auth/user-manager";
import { useAuth } from "@/hooks/use-auth";
import {
  storedClientPick,
  useActiveClient,
} from "@/lib/active-client";
import { listSeedClientEntries } from "@/lib/seed-clients";
import { can } from "@/lib/auth/can";
import { runStorageMigration } from "@/lib/storage-migration";
import { useAssetLocker } from "@/stores/useAssetLocker";
import { useCaptures } from "@/stores/useCaptures";
import { ClientSelectGate } from "./ClientSelectGate";

export function ClientProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { resolved, resolve } = useActiveClient();
  const hydratedRef = useRef(false);

  // ── 1. Resolution ──────────────────────────────────────────────────
  useEffect(() => {
    if (resolved) return;
    if (!isMultiClientEnabled()) {
      resolve({ clientId: null, source: "none" });
      return;
    }
    if (!isZitadelMode()) {
      // Seed/offline dev: a stored pick (the facilitator switcher works
      // offline against the stub registry) or the default stub client.
      const pick = storedClientPick();
      const stub = listSeedClientEntries().find((c) => c.id === pick);
      if (pick) {
        resolve({ clientId: pick, clientName: stub?.name ?? pick, source: "picked" });
      } else {
        resolve({ clientId: "local", clientName: "Local Dev", source: "seed" });
      }
      return;
    }
    if (!user) return; // login pending — LoginPage renders below, no hydration
    if (can(user, "switch:client")) {
      const pick = storedClientPick();
      if (pick) resolve({ clientId: pick, source: "picked" });
      // no pick → stay unresolved; the gate below takes over.
      return;
    }
    // Client user: org id keys the local caches; the BFF scopes the data.
    void getCurrentUser().then((oidcUser) => {
      resolve({ clientId: clientOrgIdFromClaims(oidcUser), source: "org" });
    });
  }, [resolved, resolve, user]);

  // ── 2. Boot hydration, strictly post-resolution ────────────────────
  useEffect(() => {
    if (!resolved || hydratedRef.current) return;
    hydratedRef.current = true;
    // One-shot rename of `xaes2-*` legacy persistence keys, then the boot
    // hydrates (moved here from AppProviders so they observe the client
    // scope). Idempotent; StrictMode double-fire is guarded by the ref.
    void runStorageMigration().then(() => {
      void useAssetLocker.getState().hydrate();
      void useCaptures.getState().hydrate();
    });
  }, [resolved]);

  if (!resolved && user && isMultiClientEnabled() && can(user, "switch:client")) {
    return <ClientSelectGate />;
  }
  return <>{children}</>;
}
